import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { HiSwitchHorizontal, HiRefresh, HiOutlineCalendar } from 'react-icons/hi';
import apiService from '../services/api';
import { useNotification } from '../hooks/useNotification';
import Notification from './Notification';
import Select from './Select';
import {
  CURRENCY_META,
  CURRENCY_CODES,
  CURRENCY_OPTIONS,
  formatMoney,
  isZeroDecimalCurrency,
  maskAmountInput,
  maskFromNumber,
  parseMaskedAmount,
  roundByCurrency,
} from '../utils/currencies';
import './CurrencyConverter.css';

/** Data de hoje em America/Sao_Paulo no formato YYYY-MM-DD. */
const todayIsoSaoPaulo = () => {
  try {
    return new Intl.DateTimeFormat('en-CA', {
      timeZone: 'America/Sao_Paulo',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(new Date());
  } catch {
    return new Date().toISOString().slice(0, 10);
  }
};

const crossRate = (rates, from, to) => {
  const fromPerBrl = from === 'BRL' ? 1 : Number(rates?.[from]);
  const toPerBrl = to === 'BRL' ? 1 : Number(rates?.[to]);
  if (!(fromPerBrl > 0) || !(toPerBrl > 0)) return null;
  return toPerBrl / fromPerBrl;
};

/** Formata YYYY-MM-DD para exibição discreta (pt-BR). */
const formatQuoteLabel = (iso) => {
  if (!iso) return '—';
  const today = todayIsoSaoPaulo();
  if (iso === today) return 'Hoje';
  const [y, m, d] = iso.split('-');
  if (!y || !m || !d) return iso;
  return `${d}/${m}/${y}`;
};

const flagCode = (code) => CURRENCY_META[code]?.flag || '';

const CurrencyConverter = () => {
  const { notifications, removeNotification, showWarning } =
    useNotification();
  const [today] = useState(() => todayIsoSaoPaulo());
  const [quoteDate, setQuoteDate] = useState(() => todayIsoSaoPaulo());
  const [amount, setAmount] = useState(() => maskFromNumber(100, 'BRL'));
  const [fromCurrency, setFromCurrency] = useState('BRL');
  const [toCurrency, setToCurrency] = useState('XOF');
  const [rates, setRates] = useState(null);
  const [loadingRates, setLoadingRates] = useState(true);
  const [converting, setConverting] = useState(false);
  const [result, setResult] = useState(null);

  const loadRates = useCallback(
    async (date) => {
      const safeDate = date && date > todayIsoSaoPaulo() ? todayIsoSaoPaulo() : date;
      try {
        setLoadingRates(true);
        const response = await apiService.getCurrencyRates(safeDate || undefined);
        const data = response?.data || response;
        const normalizedRates = {
          ...(data || {}),
          rates: { BRL: 1, ...(data?.rates || {}) },
        };
        setRates(normalizedRates);
      } catch (error) {
        showWarning(error?.message || 'Erro ao carregar câmbio');
        setRates(null);
      } finally {
        setLoadingRates(false);
      }
    },
    [showWarning]
  );

  useEffect(() => {
    loadRates(quoteDate);
  }, [loadRates, quoteDate]);

  const changeQuoteDate = (next) => {
    const max = todayIsoSaoPaulo();
    const value = !next || next > max ? max : next;
    setQuoteDate(value);
    setResult(null);
  };

  const preview = useMemo(() => {
    const value = parseMaskedAmount(amount, fromCurrency);
    const rate = crossRate(rates?.rates, fromCurrency, toCurrency);
    if (value == null || value < 0 || rate == null) return null;
    return {
      amount: value,
      from: fromCurrency,
      to: toCurrency,
      rate,
      converted: roundByCurrency(value * rate, toCurrency),
      date: rates?.date,
    };
  }, [amount, fromCurrency, toCurrency, rates]);

  const changeFromCurrency = (next) => {
    const numeric = parseMaskedAmount(amount, fromCurrency);
    setFromCurrency(next);
    if (numeric != null) {
      setAmount(maskFromNumber(numeric, next));
    } else {
      setAmount('');
    }
    setResult(null);
  };

  const swapCurrencies = () => {
    const numeric = parseMaskedAmount(amount, fromCurrency);
    setFromCurrency(toCurrency);
    setToCurrency(fromCurrency);
    if (numeric != null) {
      setAmount(maskFromNumber(numeric, toCurrency));
    }
    setResult(null);
  };

  const handleConvert = async (e) => {
    e.preventDefault();
    const value = parseMaskedAmount(amount, fromCurrency);
    if (value == null || value < 0) {
      showWarning('Informe um valor válido');
      return;
    }
    const from = String(fromCurrency || '').toUpperCase();
    const to = String(toCurrency || '').toUpperCase();
    if (!CURRENCY_CODES.includes(from) || !CURRENCY_CODES.includes(to)) {
      showWarning('Selecione moedas válidas');
      return;
    }
    if (from === to) {
      showWarning('Escolha moedas diferentes para converter');
      return;
    }
    try {
      setConverting(true);
      const response = await apiService.convertCurrency({
        amount: value,
        from,
        to,
        from_currency: from,
        to_currency: to,
        date: quoteDate,
      });
      const data = response?.data || response;
      setResult(data);
    } catch (error) {
      showWarning(error?.message || 'Erro ao converter');
    } finally {
      setConverting(false);
    }
  };

  const displayFrom = result?.from || fromCurrency;
  const displayTo = result?.to || result?.currency || toCurrency;
  const displayConverted = result?.amount_converted ?? preview?.converted ?? 0;
  const displayAmountFrom = result?.amount_from ?? preview?.amount ?? 0;
  const displayRate = result?.rate ?? preview?.rate ?? 0;
  const displayQuoteDate = result?.date || preview?.date || rates?.date || quoteDate;

  return (
    <div className="page-container currency-page">
      <div className="notifications-container">
        {notifications.map((notification) => (
          <Notification
            key={notification.id}
            message={notification.message}
            type={notification.type}
            duration={notification.duration}
            onClose={() => removeNotification(notification.id)}
          />
        ))}
      </div>

      <section className="currency-hero">
        <div className="currency-hero-left">
          <div className="currency-hero-icon">
            <HiSwitchHorizontal size={28} />
          </div>
          <div>
            <h1>Conversor de Moedas</h1>
            <p>
              Converta entre Real, Euro, Dólar, CFA, Kwanza, Escudo e Franco congolês
              com câmbio do dia escolhido.
            </p>
          </div>
        </div>
        <button
          type="button"
          className="currency-refresh"
          onClick={() => loadRates(quoteDate)}
          disabled={loadingRates}
        >
          <HiRefresh size={18} className={loadingRates ? 'is-spinning' : ''} />
          {loadingRates ? 'Atualizando…' : 'Atualizar câmbio'}
        </button>
      </section>

      <div className="currency-grid">
        <form className="currency-card" onSubmit={handleConvert}>
          <div className="currency-card-head">
            <h2>Converter</h2>
            <div className="currency-date-picker">
              <HiOutlineCalendar size={15} aria-hidden />
              <span className="currency-date-picker-label">
                {formatQuoteLabel(quoteDate)}
              </span>
              <input
                id="currency-quote-date"
                type="date"
                value={quoteDate}
                max={today}
                onChange={(e) => changeQuoteDate(e.target.value)}
                aria-label="Data da cotação"
                title="Escolher data da cotação"
              />
              {quoteDate !== today && (
                <button
                  type="button"
                  className="currency-date-today"
                  onClick={() => changeQuoteDate(today)}
                >
                  Hoje
                </button>
              )}
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="currency-amount">Valor</label>
            <div className="currency-amount-field">
              <span className="currency-amount-flag" aria-hidden>
                {flagCode(fromCurrency)}
              </span>
              <span className="currency-amount-symbol">
                {CURRENCY_META[fromCurrency]?.symbol || ''}
              </span>
              <input
                id="currency-amount"
                type="text"
                inputMode="numeric"
                value={amount}
                onChange={(e) => {
                  setAmount(maskAmountInput(e.target.value, fromCurrency));
                  setResult(null);
                }}
                placeholder={isZeroDecimalCurrency(fromCurrency) ? '0' : '0,00'}
                autoComplete="off"
              />
              <span className="currency-amount-code">{fromCurrency}</span>
            </div>
            <small className="currency-amount-hint">
              Digite apenas números — a máscara formata automaticamente
            </small>
          </div>

          <div className="currency-pair-row">
            <div className="form-group currency-pair-field">
              <label>De</label>
              <Select
                value={fromCurrency}
                placeholder=""
                onChange={(e) => changeFromCurrency(e.target.value)}
                options={CURRENCY_OPTIONS}
              />
            </div>
            <div className="currency-swap-wrap">
              <button
                type="button"
                className="currency-swap"
                onClick={swapCurrencies}
                title="Inverter moedas"
                aria-label="Inverter moedas"
              >
                <HiSwitchHorizontal size={20} />
              </button>
            </div>
            <div className="form-group currency-pair-field">
              <label>Para</label>
              <Select
                value={toCurrency}
                placeholder=""
                onChange={(e) => {
                  setToCurrency(e.target.value);
                  setResult(null);
                }}
                options={CURRENCY_OPTIONS}
              />
            </div>
          </div>

          <button type="submit" className="currency-submit" disabled={converting}>
            {converting ? 'Convertendo…' : 'Converter agora'}
          </button>
        </form>

        <div className="currency-card currency-card--result">
          <p className="currency-eyebrow">Resultado</p>
          <p className="currency-route">
            <span>
              {flagCode(displayFrom)} {displayFrom}
            </span>
            <span aria-hidden> → </span>
            <span>
              {flagCode(displayTo)} {displayTo}
            </span>
          </p>
          <div className="currency-amount-big">
            <span className="currency-amount-big-flag" aria-hidden>
              {flagCode(displayTo)}
            </span>
            {formatMoney(displayConverted, displayTo)}
          </div>
          <p className="currency-from">
            de {flagCode(displayFrom)} {formatMoney(displayAmountFrom, displayFrom)}
          </p>
          <div className="currency-meta">
            <span>
              Taxa: 1 {displayFrom} ={' '}
              {Number(displayRate).toLocaleString('pt-BR', {
                maximumFractionDigits: 6,
              })}{' '}
              {displayTo}
            </span>
            <span>Cotação: {displayQuoteDate}</span>
          </div>
        </div>
      </div>

      <div className="currency-rates">
        <h2>Câmbio de {displayQuoteDate} (referência 1 BRL)</h2>
        {loadingRates && !rates ? (
          <p className="currency-muted">Carregando cotações…</p>
        ) : (
          <div className="currency-rates-grid">
            {Object.entries(CURRENCY_META).map(([code, meta]) => (
              <div key={code} className="currency-rate-item">
                <strong>
                  <span aria-hidden>{meta.flag}</span> {meta.symbol} {code}
                </strong>
                <span>{meta.label}</span>
                <em>
                  1 BRL ={' '}
                  {(rates?.rates?.[code] ?? (code === 'BRL' ? 1 : 0)).toLocaleString(
                    'pt-BR',
                    { maximumFractionDigits: 6 }
                  )}
                </em>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default CurrencyConverter;
